// NPM Imports
import React from "react";
import ReactHtmlParser from "react-html-parser";
import { ContentState, convertToRaw } from "draft-js";
import { Input } from "@revvsales/components";

// Components Imports
import CustomRevvEditor from "../../CustomRevvEditor";
import HTMLtoDraft from "../../../utils/HTMLtoDraft/HTMLtoDraft";

// Internal Imports
import { TEMPLATE, DOCUMENTS } from "../../../constants";
import {
  getClauseOptionView,
  getClauseOptionValue,
} from "../../../utils/documentUtils";

// Destructuring
const { CLAUSES_OPTIONS } = TEMPLATE;
const { DOCUMENT_MODE } = DOCUMENTS;

class MultiSelectForm {
  constructor(props) {
    const {
      meta: { clauses },
      value,
      docIsLocked,
      updateClauseFieldValue,
      filledStatus,
    } = props;
    this.value = value;
    this.type = "checkbox";
    this.clauses = clauses;
    this.docIsLocked = docIsLocked;
    this.updateClauseFieldValue = updateClauseFieldValue;
    this.filledStatus = filledStatus;
  }

  _updateFormValue(optionID) {
    let optionIDs = [];

    if (!this.value || !Array.isArray(this.value)) {
      optionIDs = [optionID];
    } else if (this.value.map(v => v.id).includes(optionID)) {
      optionIDs = this.value.filter(v => v.id !== optionID).map(v => v.id);
    } else {
      optionIDs = [...this.value.map(v => v.id), optionID];
    }
    const updatedValue = this.clauses.filter(clause =>
      optionIDs.includes(clause.id),
    );
    this.updateClauseFieldValue(updatedValue);
    this._checkFilledMultiSelect(updatedValue);
  }

  _checkFilledMultiSelect(updatedValue) {
    if (updatedValue.length >= 1) {
      this.filledStatus(true);
    } else {
      this.filledStatus(false);
    }
  }

  _getView() {
    const viewList = this.clauses.map(option => (
      <li className="document-clause-form-select">
        <div className="document-clause-form-select-box">
          <Input
            type={this.type}
            data-test="input-revv-inp-checkbox-or-radio"
            checked={
              Array.isArray(this.value) &&
              this.value.map(v => v.id).includes(option.id)
            }
            onChange={() => this._updateFormValue(option.id)}
            disabled={this.docIsLocked}
          />
          <div className="document-clause-value-view">
            {ReactHtmlParser(getClauseOptionView(option.value))}
          </div>
        </div>
      </li>
    ));

    return <div className="document-clause-form-select-list">{viewList}</div>;
  }

  view() {
    return (
      <div className="document-clause-form-view">
        <span className="document-clause-option-info">
          {`(You can choose 1 or more out of the availabe  ${
            this.clauses.length
          } options for this clause)`}
        </span>
        {this._getView()}
      </div>
    );
  }
}

class SingleSelectForm {
  constructor(props) {
    const {
      meta: { clauses },
      value,
      docIsLocked,
      updateClauseFieldValue,
      filledStatus,
    } = props;
    this.type = "radio";
    this.value = value;
    this.clauses = clauses;
    this.docIsLocked = docIsLocked;
    this.updateClauseFieldValue = updateClauseFieldValue;
    this.filledStatus = filledStatus;
  }

  _updateFormValue(optionID) {
    const optionValue = this.clauses.find(clause => clause.id === optionID);

    if (!optionValue) return;

    this.updateClauseFieldValue(optionValue);
    this.filledStatus(true);
  }

  _getView() {
    const viewList = this.clauses.map(option => (
      <li className="document-clause-form-select">
        <div className="document-clause-form-select-box">
          <Input
            type={this.type}
            data-test="input-revv-inp-checkbox-or-radio"
            checked={this.value && this.value.id === option.id}
            onChange={() => this._updateFormValue(option.id)}
            disabled={this.docIsLocked}
          />
          <div className="document-clause-value-view">
            {ReactHtmlParser(getClauseOptionView(option.value))}
          </div>
        </div>
      </li>
    ));

    return <div className="document-clause-form-select-list">{viewList}</div>;
  }

  view() {
    return (
      <div className="document-clause-form-view">
        <span className="document-clause-option-info">
          {`(You can choose 1 out of the available ${
            this.clauses.length
          } options for this clause)`}
        </span>
        {this._getView()}
      </div>
    );
  }
}

class OptionalForm {
  constructor(props) {
    const {
      meta: { clauses },
      value,
      docIsLocked,
      updateClauseFieldValue,
      filledStatus,
    } = props;
    this.value = value;
    this.type = "checkbox";
    this.clauses = clauses;
    this.docIsLocked = docIsLocked;
    this.updateClauseFieldValue = updateClauseFieldValue;
    this.filledStatus = filledStatus;
  }

  _updateFormValue(optionID) {
    let optionIDs = [];

    if (!this.value || !Array.isArray(this.value)) {
      optionIDs = [optionID];
    } else if (this.value.map(v => v.id).includes(optionID)) {
      optionIDs = this.value.filter(v => v.id !== optionID).map(v => v.id);
    } else {
      optionIDs = [...this.value.map(v => v.id), optionID];
    }
    const updatedValue = this.clauses.filter(clause =>
      optionIDs.includes(clause.id),
    );
    this.updateClauseFieldValue(updatedValue);
    this._checkFilledMultiSelect(updatedValue);
  }

  _checkFilledMultiSelect(updatedValue) {
    if (updatedValue.length >= 1) {
      this.filledStatus(true);
    } else {
      this.filledStatus(false);
    }
  }

  _getView() {
    const viewList = this.clauses.map(option => (
      <li className="document-clause-form-select">
        <div className="document-clause-form-select-box">
          <Input
            type={this.type}
            data-test="input-revv-inp-checkbox-or-radio"
            checked={
              Array.isArray(this.value) &&
              this.value.map(v => v.id).includes(option.id)
            }
            onChange={() => this._updateFormValue(option.id)}
            disabled={this.docIsLocked}
          />
          <div className="document-clause-value-view">
            {ReactHtmlParser(getClauseOptionView(option.value))}
          </div>
        </div>
      </li>
    ));
    return <div className="document-clause-form-select-list">{viewList}</div>;
  }

  view() {
    return (
      <div className="document-clause-form-view">
        <span className="document-clause-option-info">
          {`(There are ${
            this.clauses.length
          } options available for this clause. This is optional though.)`}
        </span>
        {this._getView()}
      </div>
    );
  }
}

class FreeTextForm {
  constructor(props) {
    const {
      formValue,
      value,
      updateClauseFieldValue,
      docIsLocked,
      updateFormValue,
      filledStatus,
    } = props;
    this.formValue = formValue;
    this.updateFormValue = updateFormValue;
    this.docIsLocked = docIsLocked;
    this.updateClauseFieldValue = updateClauseFieldValue;
    this.value = value;
    this.filledStatus = filledStatus;
  }

  _changeMode = () => {
    let data;
    const value = this.value && this.value.value ? this.value.value : "";
    if (value) {
      data = HTMLtoDraft(value, DOCUMENT_MODE.PREVIEW);
    } else {
      data = ContentState.createFromText("");
    }
    this.updateFormValue(data);
  };

  _updateFormValue = sec => {
    if (sec && sec.data && sec.html && sec.data instanceof ContentState) {
      const value = JSON.stringify(convertToRaw(sec.data));
      this.updateClauseFieldValue({ value });
      this.updateFormValue(null);
      if (value) {
        this.filledStatus(true);
      } else {
        this.filledStatus(false);
      }
    }
  };

  _getEditView() {
    const value = this.value && this.value.value ? this.value.value : "";

    const section = {
      ...getClauseOptionValue(value),
    };

    return (
      <CustomRevvEditor
        section={section}
        isComponentActive
        mode={DOCUMENT_MODE.PREVIEW}
        handleBlur={this._updateFormValue}
        isDocCustomTextEditor={false}
        isCustomTableTextEditor={false}
        isSeparateToolbar={false}
      />
    );
  }

  _getValueView() {
    let htmlView =
      this.value && this.value.value
        ? getClauseOptionView(this.value.value)
        : null;
    if (!htmlView) {
      htmlView = "<p> Add text here </p>";
    }
    return (
      <div
        className="document-clause-form-value"
        onClick={!this.docIsLocked ? () => this._changeMode() : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}>
        {ReactHtmlParser(htmlView)}
      </div>
    );
  }

  view() {
    return (
      <div className="document-clause-form-view">
        {this.formValue instanceof ContentState
          ? this._getEditView()
          : this._getValueView()}
      </div>
    );
  }
}

function ClauseForm(props) {
  const {
    meta: { subtype },
  } = props;

  function getForm() {
    switch (subtype) {
      case CLAUSES_OPTIONS.OPTIONAL:
        return new OptionalForm({ ...props });
      case CLAUSES_OPTIONS.SINGLE_SELECT:
        return new SingleSelectForm({ ...props });
      case CLAUSES_OPTIONS.MULTI_SELECT:
        return new MultiSelectForm({ ...props });
      case CLAUSES_OPTIONS.FREE_TEXT:
        return new FreeTextForm({ ...props });
      default:
        return null;
    }
  }
  const form = getForm();
  const view = form.view();
  return view;
}

export default ClauseForm;



// WEBPACK FOOTER //
// ./src/components/DocumentV2/FormView/ClauseForm.js